import React, { useState, useEffect, useRef } from 'react';
import { playClick, playNav } from '../../../utils/sounds';
import './projects_main.css';

// Image imports (existing assets)
import SandRd from '../../../assets/images/31SANDRD.png';
import WillowValley from '../../../assets/images/WILLOWVALLEYOSS.png';
import Woodbine15021 from '../../../assets/images/15021WOODBINE.png';
import Woodbine23049 from '../../../assets/images/23049WOODBINE.png';
import OldTowerhill from '../../../assets/images/420OLDTOWERHILL.png';
import Countryside from '../../../assets/images/5200COUNTRYSIDE.png';
import ChurchStreet from '../../../assets/images/22CHURCHSTN.png';
import ShawRoad from '../../../assets/images/shaws.png';
import Catchet from '../../../assets/images/catchethomes.png';
import Huronia from '../../../assets/images/HURONIADD.png';
import Driftwood from '../../../assets/images/DRIFTWOOD.png';
import Meadowpine from '../../../assets/images/MEADOWPINE.png';
import Trendz from '../../../assets/images/trendz.png';
import Trendz2 from '../../../assets/images/trendz2.png';
import Sidu from '../../../assets/images/sidu.png';
import TicTacToe from '../../../assets/images/tictactoe.png';

/* ── PROJECT DATA ── */

const aiProjects = [
  {
    id: 'ai-review',
    title: 'AI Review Agents',
    description:
      'Led collaboration between Crozier and UofT ML students to develop a local CNN for automated pipe coverage review on engineering drawings and CCTV inspection analysis using open-source models. Defined automation workflows and identified critical variables for defect detection accuracy.',
    tags: ['Python', 'Machine Learning', 'CNN', 'Automation'],
  },
  {
    id: 'dd-ai',
    title: 'Due Diligence AI',
    description:
      'Designed a preliminary AI model for a targeted city to assist engineers with due diligence by collecting data and generating instant reports on development serviceability.',
    tags: ['Python', 'AI', 'Data Analysis', 'Civil Engineering'],
  },
  {
    id: 'forecast',
    title: 'Project Forecasting Tool',
    description:
      'Implemented office-wide Excel database to track ongoing Crozier projects and weekly workloads including forecasting future workloads, streamlining productivity, and supporting data-driven decisions for resource allocation.',
    tags: ['Excel', 'VBA', 'Data Visualization', 'Project Management'],
  },
];

const techProjects = [
  {
    id: 't-1',
    title: '$SIDU Day Moving Average Calculator',
    image: Sidu,
    description:
      'A calculator that computes the day moving average of $SIDU, then displays a chart of the stock\'s price alongside the 5, 10, 20 and 50 day moving averages.',
    tags: ['Python', 'SQL'],
    github: 'https://github.com/Amrinder-B/-SIDU',
  },
  {
    id: 't-2',
    title: 'Stock Trends & Financials',
    image: Trendz,
    image2: Trendz2,
    description:
      'A trend analysis tool that uses Google Trends & Yahoo Finance APIs to help find stocks that have a low P/E but significant popularity.',
    tags: ['Python', 'Flask', 'HTML', 'CSS', 'JavaScript'],
    github: 'https://github.com/Amrinder-B/StockTrendz',
  },
  {
    id: 't-3',
    title: 'HTML Games',
    image: TicTacToe,
    description:
      'Collection of browser games (Tic Tac Toe, Snake, Pong) built to practice UI design and coding logic.',
    tags: ['HTML', 'CSS', 'JavaScript', 'Figma'],
    github: 'https://github.com/Amrinder-B/tictactoe',
  },
];

const civilProjects = [
  { id: 'c-1', title: 'Sand Rd \u2014 ZBA/SPA', image: SandRd, description: 'Worked in a team of 3, serving as the designer for stormwater management, grading, and servicing elements of an 88-unit condominium subdivision for SPA & ZBA.', location: 'East Gwillimbury, ON', categories: ['Site Plan Approval', 'Zoning By-law Amendment'] },
  { id: 'c-2', title: 'Towerhill Rd \u2014 ZBA/DPoS', image: OldTowerhill, description: 'Worked in a team of 4, serving as the designer for grading and servicing elements of a proposed 12-unit residential subdivision for SPA & ZBA.', location: 'Peterborough, ON', categories: ['Draft Plan of Subdivision', 'Zoning By-law Amendment'] },
  { id: 'c-3', title: 'Willow Valley \u2014 OSS', image: WillowValley, description: 'Designed an on-site sewage system directly under a P.Eng for the Willow Valley Golf Course, submitted for SPA.', location: 'Mount Hope, ON', categories: ['On-site Sewage System', 'Site Plan Approval'] },
  { id: 'c-4', title: 'Huronia \u2014 Due Diligence', image: Huronia, description: 'Performed due diligence on a property for the feasibility of a 12-unit industrial subdivision. Inspired the creation of an automated due diligence tool for Crozier.', location: 'Barrie, ON', categories: ['Due Diligence', 'Industrial'] },
  { id: 'c-5', title: 'Countryside \u2014 Class 5 OSS', image: Countryside, description: 'Designed a Class 5 on-site sewage system directly under a P.Eng for a commercial property.', location: 'Brampton, ON', categories: ['Class 5 Sewage System', 'Commercial'] },
  { id: 'c-6', title: 'Church Street \u2014 ZBA/SPA', image: ChurchStreet, description: 'Served as a designer for the grading of a house conversion to office space, submitted for both ZBA and SPA.', location: 'Ajax, ON', categories: ['Site Plan Approval', 'Zoning By-law Amendment'] },
  { id: 'c-7', title: 'Driftwood \u2014 OSS (MECP/SPA)', image: Driftwood, description: 'Assisted in the design of an on-site septic system for a mobile home park. Capacity above 10,000 L/day, requiring both SPA and MECP approvals.', location: 'Bluewater (Zurich), ON', categories: ['On-site Sewage System', 'MECP Approval'] },
  { id: 'c-8', title: 'Meadowpine \u2014 Due Diligence', image: Meadowpine, description: 'Due diligence for the feasibility of an industrial warehouse.', location: 'Mississauga, ON', categories: ['Due Diligence', 'Industrial'] },
  { id: 'c-9', title: 'Woodbine Ave \u2014 OSS (SPA)', image: Woodbine23049, description: 'Designed an on-site sewage system under a P.Eng for a proposed maintenance facility and office space.', location: 'Town of Georgina, ON', categories: ['On-site Sewage System', 'Site Plan Approval'] },
  { id: 'c-10', title: 'Woodbine Ave \u2014 Type A (MECP)', image: Woodbine15021, description: 'Designed a Type A dispersal bed under a P.Eng for a proposed office and warehouse. Capacity above 10,000 L/day, requiring SPA and MECP approvals.', location: 'Gormley, ON', categories: ['Type A Dispersal Bed', 'MECP Approval'] },
  { id: 'c-11', title: "Shaw's Road \u2014 Septic Assessment", image: ShawRoad, description: 'Performed a septic assessment and determined system usability for a residential property.', location: 'The Blue Mountains, ON', categories: ['Septic Assessment', 'Residential'] },
  { id: 'c-12', title: 'Amaranth \u2014 OSS (BP)', image: Catchet, description: 'Designed on-site sewage systems for each lot of a proposed 73-unit residential subdivision. Submissions for SPA and BP required.', location: 'Amaranth, ON', categories: ['On-site Sewage System', 'Building Permit'] },
];

const TABS = [
  { key: 'ai', label: 'AI & AUTOMATION' },
  { key: 'civil', label: 'CIVIL ENGINEERING' },
  { key: 'dev', label: 'CODING PROJECTS' },
];

/* ── SLIDESHOW ── */
const Slideshow = ({ projects, showGithub }) => {
  const [idx, setIdx] = useState(0);
  const timerRef = useRef(null);

  const resetTimer = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => setIdx((p) => (p + 1) % projects.length), 12000);
  };

  useEffect(() => {
    resetTimer();
    return () => clearInterval(timerRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projects.length]);

  const go = (dir) => {
    playClick();
    setIdx((p) => (p + dir + projects.length) % projects.length);
    resetTimer();
  };

  const proj = projects[idx];

  return (
    <div className="slideshow">
      <div className="slide-outer">
        {/* Fixed side arrows — always in the same spot */}
        <button className="slide-arrow slide-arrow-prev" onClick={() => go(-1)} aria-label="Previous">&lt;</button>

        <div className="slide-viewport">
          <div className="slide-image-wrap">
            {proj.image && <img src={proj.image} alt={proj.title} className="slide-img" />}
            <div className="viewport-label">
              [{String(idx + 1).padStart(2, '0')}/{String(projects.length).padStart(2, '0')}]
            </div>
          </div>

          <div className="slide-info">
            <h3 className="slide-title">{proj.title}</h3>
            <p className="slide-desc">{proj.description}</p>

            {proj.categories && (
              <div className="slide-tags">
                {proj.categories.map((c) => (
                  <span key={c} className="tag">{c}</span>
                ))}
              </div>
            )}
            {proj.tags && (
              <div className="slide-tags">
                {proj.tags.map((t) => (
                  <span key={t} className="tag">{t}</span>
                ))}
              </div>
            )}

            {proj.location && (
              <div className="slide-location">{'\u25C6'} {proj.location}</div>
            )}

            {showGithub && proj.github && (
              <a href={proj.github} target="_blank" rel="noopener noreferrer" className="crt-button slide-github">
                VIEW ON GITHUB {'\u2192'}
              </a>
            )}
          </div>
        </div>

        <button className="slide-arrow slide-arrow-next" onClick={() => go(1)} aria-label="Next">&gt;</button>
      </div>

      {/* Dot indicators below */}
      <div className="slide-dots-row">
        {projects.map((_, i) => (
          <button
            key={i}
            className={`dot-btn ${i === idx ? 'active' : ''}`}
            onClick={() => { playClick(); setIdx(i); resetTimer(); }}
            aria-label={`Slide ${i + 1}`}
          />
        ))}
      </div>
    </div>
  );
};

/* ── AI PROJECT LIST (no images) ── */
const AiProjectList = ({ projects }) => (
  <div className="ai-project-list">
    {projects.map((proj) => (
      <div key={proj.id} className="terminal-window ai-card">
        <div className="terminal-header">
          <div className="dots">
            <span className="dot active"></span>
            <span className="dot"></span>
            <span className="dot"></span>
          </div>
          <span>{proj.id.replace(/-/g, '_')}.proj</span>
        </div>
        <div className="terminal-body">
          <h3 className="ai-title">{proj.title}</h3>
          <p className="ai-desc">{proj.description}</p>
          <div className="slide-tags">
            {proj.tags.map((t) => (
              <span key={t} className="tag amber">{t}</span>
            ))}
          </div>
        </div>
      </div>
    ))}
  </div>
);

/* ── MAIN COMPONENT ── */
export const ProjectsOverview = () => {
  const [tab, setTab] = useState('ai');
  const [visible, setVisible] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) setVisible(true); },
      { threshold: 0.03 }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);

  const switchTab = (key) => {
    playNav();
    setTab(key);
  };

  return (
    <section
      id="Projects"
      className={`projects-section ${visible ? 'visible' : ''}`}
      ref={ref}
      aria-label="Projects"
    >
      <div className="container">
        <div className="section-header">
          <h2>PROJECTS</h2>
        </div>

        {/* Tabs */}
        <div className="proj-tabs">
          {TABS.map((t) => (
            <button
              key={t.key}
              className={`proj-tab ${tab === t.key ? 'active' : ''}`}
              onClick={() => switchTab(t.key)}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="proj-content">
          {tab === 'ai' && <AiProjectList projects={aiProjects} />}
          {tab === 'civil' && (
            <div className="terminal-window slideshow-terminal">
              <div className="terminal-header">
                <div className="dots">
                  <span className="dot active"></span><span className="dot"></span><span className="dot"></span>
                </div>
                <span>civil_projects.cad</span>
              </div>
              <div className="terminal-body">
                <Slideshow projects={civilProjects} showGithub={false} />
              </div>
            </div>
          )}
          {tab === 'dev' && (
            <div className="terminal-window slideshow-terminal">
              <div className="terminal-header">
                <div className="dots">
                  <span className="dot active"></span><span className="dot"></span><span className="dot"></span>
                </div>
                <span>dev_projects.src</span>
              </div>
              <div className="terminal-body">
                <Slideshow projects={techProjects} showGithub={true} />
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default ProjectsOverview;
